/**
 * `TeamMemberDashboard` — dashboard di un membro del Team, in **sola lettura**.
 *
 * Drill-down dalla sezione "Livelli" (vista Team): cliccando una persona si apre
 * la sua dashboard così come la vedrebbe lei stessa (KPI + card competenze),
 * senza però poter modificare i voti. Ricalca la struttura di `Dashboard.tsx`,
 * ma:
 *  - carica i dati da `GET /api/dashboard/users/:userId`
 *    ({@link TeamMemberDashboardDto}: identità della persona + `DashboardDto`);
 *  - mostra un'intestazione con nome/reparto della persona e un'azione per
 *    tornare al Team;
 *  - le card competenza aprono il dettaglio nel contesto Team
 *    (`/team/:userId/competences/:competenceId`).
 *
 * L'accesso è aperto a ogni ruolo autenticato (come il resto della sezione
 * Livelli); l'eventuale restrizione sarà applicata all'intera sezione.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { CompetencyMatchCard } from '../components/CompetencyMatchCard';
import { JobMatchCard } from '../components/JobMatchCard';
import {
  CertificationCard,
  sortCertificationsByIssuedDesc,
} from '../components/CertificationCard';
import { ScoreGuide } from '../components/ScoreGuide';
import { JobScoreGuide } from '../components/JobScoreGuide';
import { LivelloGuide } from '../components/LivelloGuide';
import { ScoreCalcGuide } from '../components/ScoreCalcGuide';
import { ScoreDetailModal } from '../components/ScoreDetailModal';
import { ABSENT_INDICATOR } from '../components/ScoreTriple';
import { ScoreBadge } from '../components/ScoreBadge';
import { CertificationCountBadge } from '../components/CertificationCountBadge';
import { propensionLabel } from '../components/PropensionSelect';
import { LivelloBadge } from '../components/LivelloBadge';
import { reviewsLevelRank } from '../components/SkillAssessmentTable';
import type {
  AssessmentView,
  CompetencyMatch,
  ExperienceRange,
  ImportCredlyResponse,
  ImportLinkedinResponse,
  JobMatch,
  JobPositioningDto,
  Livello,
  ScoringConfigDto,
  SkillDto,
  TeamMemberDashboardDto,
  UserCertificationDto,
} from '../shared/dto';
import {
  DEFAULT_SCORING_CONFIG,
  EXPERIENCE_RANGE_LABELS,
  EXPERIENCE_RANGE_VALUES,
  LIVELLO_VALUES,
  livelloRank,
} from '../shared/dto';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  InfoHint,
  Loading,
  PageHeader,
  Skeleton,
  type Column,
  type SortDirection,
} from '../components/ui';

/**
 * Numero di competenze mostrate nella griglia sotto la hero (la competenza
 * principale). Totale in evidenza = 1 (hero) + `GRID_MATCHES_COUNT`.
 */
const GRID_MATCHES_COUNT = 3;

/** Numero di professioni principali mostrate in griglia (prima dell'accordion). */
const JOB_GRID_COUNT = 3;

/** Numero di skill (Score più alto) sempre visibili; le altre in accordion. */
const TOP_SKILLS_COUNT = 10;

/** Numero di certificazioni sempre visibili; le altre dietro l'accordion. */
const TOP_CERTIFICATIONS_COUNT = 3;

/** Ordina i match per `rankScore` decrescente, con `matchPercent` come tie-breaker. */
function sortByMatchDesc(matches: CompetencyMatch[]): CompetencyMatch[] {
  return [...matches].sort(
    (a, b) => b.rankScore - a.rankScore || b.matchPercent - a.matchPercent,
  );
}

/** Ordina i job per `rankScore` decrescente, con `matchPercent` come tie-breaker. */
function sortJobsByMatchDesc(jobs: JobMatch[]): JobMatch[] {
  return [...jobs].sort(
    (a, b) => b.rankScore - a.rankScore || b.matchPercent - a.matchPercent,
  );
}

/** Numero totale di recensioni (reviewer + manager) considerate nel rango. */
function reviewsCount(row: AssessmentView): number {
  return row.reviewerCount + row.managerCount;
}

/**
 * Converte un rango continuo `[0,5]` nel Livello più vicino (arrotondamento al
 * valore ordinale più prossimo), così da poterlo mostrare come `LivelloBadge`.
 * Il rango è 0-based (`0 → e-0` "Nessuno" … `5 → e-5`), coerente con
 * `livelloRank` dei DTO condivisi.
 */
function rankToLivello(rank: number): Livello {
  const index = Math.min(
    LIVELLO_VALUES.length - 1,
    Math.max(0, Math.round(rank)),
  );
  return LIVELLO_VALUES[index];
}

/** Confronto lessicografico dei nomi skill (già orientato dalla direzione). */
function compareSkillName(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  if (a.skillName < b.skillName) return -sign;
  if (a.skillName > b.skillName) return sign;
  return 0;
}

/**
 * Comparatore per la colonna "Livello (Self)": ordina per rango della scala
 * (`e-1..e-5`), con i livelli assenti (`null`) sempre in coda.
 */
function compareLivelloSelf(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const la = a.livelloSelf;
  const lb = b.livelloSelf;
  if (la === null && lb === null) return 0;
  if (la === null) return 1;
  if (lb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (livelloRank(la) - livelloRank(lb));
}

/**
 * Comparatore per la colonna "AI": ordina per rango del Livello stimato dall'AI
 * (`e-1..e-5`), con i livelli assenti (`null`) sempre in coda.
 */
function compareAiLevel(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const la = a.aiLevel;
  const lb = b.aiLevel;
  if (la === null && lb === null) return 0;
  if (la === null) return 1;
  if (lb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (livelloRank(la) - livelloRank(lb));
}

/**
 * Comparatore per la colonna "Propensione" (`selfInterest`): il
 * valore `0` è valido, i `null` sempre in coda.
 */
function compareInterest(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.selfInterest;
  const vb = b.selfInterest;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Reviews": rango di Livello aggregato, `null` in coda. */
function compareReviews(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = reviewsLevelRank(a);
  const vb = reviewsLevelRank(b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Score": Score_Skill derivato dall'API, `null` in coda. */
function compareScore(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.score;
  const vb = b.score;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Esperienza": rango della scala di fasce, `null` in coda. */
function compareExperience(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const ra = a.yearsOfExperience;
  const rb = b.yearsOfExperience;
  if (ra === null && rb === null) return 0;
  if (ra === null) return 1;
  if (rb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return (
    sign *
    (EXPERIENCE_RANGE_VALUES.indexOf(ra) - EXPERIENCE_RANGE_VALUES.indexOf(rb))
  );
}

/** Rende la fascia di anni di esperienza dichiarata, o "—" se assente. */
function experienceCell(value: ExperienceRange | null) {
  if (value === null) {
    return (
      <span className="skill-list__absent" aria-label="Esperienza: non dichiarata">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  return <span className="skill-list__years">{EXPERIENCE_RANGE_LABELS[value]}</span>;
}

/**
 * Rende la Propensione come barra di avanzamento (`progress`) sulla scala
 * `0..10`: la larghezza del riempimento è proporzionale al voto e l'etichetta
 * testuale è esposta come tooltip e testo accessibile; "—" quando assente.
 * Dato puramente informativo, reso in modo identico alla lista skill.
 */
function interestCell(value: number | null, label = 'Propensione') {
  if (value === null) {
    return (
      <span
        className="skill-list__absent"
        aria-label={`${label}: nessuna preferenza`}
      >
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const percent = (value / 10) * 100;
  const text = propensionLabel(value);
  return (
    <span
      className="skill-list__propension-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={10}
      aria-valuenow={value}
      aria-valuetext={text}
      aria-label={label}
      title={`${text} (${value}/10)`}
    >
      <span
        className="skill-list__propension-bar-fill"
        style={{ width: `${percent}%` }}
      />
    </span>
  );
}

/** Rende un Livello come badge, o "—" quando assente. */
function livelloCell(livello: Livello | null) {
  return (
    <span className="skill-list__level-cell">
      <LivelloBadge livello={livello} />
    </span>
  );
}

/**
 * Rende la colonna "Reviews": il Livello aggregato reviewer/manager come badge
 * di Livello (rango continuo arrotondato) con il numero di recensioni; "—"
 * quando nessuno ha votato.
 */
function reviewsCell(row: AssessmentView) {
  const rank = reviewsLevelRank(row);
  if (rank === null) {
    return (
      <span className="skill-list__absent" aria-label="Reviews: nessun voto">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const count = reviewsCount(row);
  return (
    <span className="skill-list__reviews">
      <LivelloBadge livello={rankToLivello(rank)} />
      <span
        className="skill-list__reviews-count"
        title={`${count} ${count === 1 ? 'recensione' : 'recensioni'}`}
        aria-label={`${count} ${count === 1 ? 'recensione' : 'recensioni'}`}
      >
        {count}
      </span>
    </span>
  );
}

/**
 * Rende lo "Score" (Score_Skill derivato) come badge colorato, o "—" se assente,
 * affiancato dal badge del numero di certificazioni valide che vi contribuiscono.
 * Se `onClick` è fornito e lo Score è presente, il badge diventa un pulsante che
 * apre il dettaglio del calcolo (come nella lista skill `/assessment/skills`).
 */
function scoreBadgeCell(row: AssessmentView, onClick?: () => void) {
  const value = row.score;
  const certBadge = (
    <CertificationCountBadge count={row.certificationCount} skillName={row.skillName} />
  );
  if (value === null) {
    return (
      <span className="skill-list__score-cell">
        <span className="skill-list__absent" aria-label="Score: assente">
          {ABSENT_INDICATOR}
        </span>
        {certBadge}
      </span>
    );
  }
  if (!onClick) {
    return (
      <span className="skill-list__score-cell">
        <ScoreBadge value={value} label="Score" />
        {certBadge}
      </span>
    );
  }
  const scoreLabel =
    value > 10
      ? `Score oltre 10 (${value.toFixed(1)})`
      : `Score ${value.toFixed(1)} su 10`;
  return (
    <span className="skill-list__score-cell">
      <button
        type="button"
        className="score-badge-button"
        onClick={onClick}
        title="Mostra come è calcolato lo Score"
        aria-label={`${scoreLabel} — mostra il dettaglio del calcolo`}
      >
        <ScoreBadge value={value} label="Score" />
      </button>
      {certBadge}
    </span>
  );
}

/**
 * Colonne in sola lettura, sul modello Livello, per l'elenco di TUTTE le skill
 * valutate della persona: Skill + Livello + Esperienza + Propensione
 * + AI (solo se abilitata) + Reviews + Score_Skill derivato. Nessuna azione né
 * edit inline (vista team read-only).
 */
function buildSkillColumns(
  aiEnabled: boolean,
  descriptionBySkillId?: Map<string, string | undefined>,
  onShowScoreDetail?: (row: AssessmentView) => void,
): Column<AssessmentView>[] {
  const columns: Column<AssessmentView>[] = [
    {
      key: 'skillName',
      header: 'Skill',
      sortable: true,
      sortComparator: compareSkillName,
      render: (row) => (
        <span className="skill-list__skill-cell">
          <span className="skill-list__skill-name">{row.skillName}</span>
          <InfoHint
            text={descriptionBySkillId?.get(row.skillId)}
            label={`Descrizione di ${row.skillName}`}
          />
        </span>
      ),
    },
    {
      key: 'livelloSelf',
      header: (
        <span className="score-calc-guide__header">
          Livello
          <LivelloGuide className="score-calc-guide__trigger" />
        </span>
      ),
      sortable: true,
      sortComparator: compareLivelloSelf,
      render: (row) => livelloCell(row.livelloSelf),
      className: 'skill-list__level-col',
    },
    {
      key: 'experience',
      header: 'Esperienza',
      sortable: true,
      sortComparator: compareExperience,
      render: (row) => experienceCell(row.yearsOfExperience),
      className: 'skill-list__experience-col',
    },
    {
      key: 'interest',
      header: 'Propensione',
      sortable: true,
      sortComparator: compareInterest,
      render: (row) => interestCell(row.selfInterest),
      className: 'skill-list__score-col skill-list__score-col--self',
    },
  ];

  if (aiEnabled) {
    columns.push({
      key: 'aiLevel',
      header: 'AI',
      sortable: true,
      sortComparator: compareAiLevel,
      render: (row) => livelloCell(row.aiLevel),
      className: 'skill-list__score-col',
    });
  }

  columns.push(
    {
      key: 'reviews',
      header: 'Reviews',
      sortable: true,
      sortComparator: compareReviews,
      render: (row) => reviewsCell(row),
      className: 'skill-list__score-col skill-list__reviews-col',
    },
    {
      key: 'score',
      header: (
        <span className="score-calc-guide__header">
          Score
          <ScoreCalcGuide />
        </span>
      ),
      sortable: true,
      sortComparator: compareScore,
      render: (row) =>
        scoreBadgeCell(
          row,
          onShowScoreDetail ? () => onShowScoreDetail(row) : undefined,
        ),
      className: 'skill-list__score-col',
    },
  );

  return columns;
}

/**
 * Skeleton mostrato mentre i dati della dashboard del membro sono in
 * caricamento. Replica la struttura reale (hero della competenza principale +
 * colonna KPI, poi la sezione "Skill valutate") così il layout non salta
 * all'arrivo dei dati. L'header con l'azione "Torna al Team" resta reale.
 *
 * I placeholder sono decorativi (`aria-hidden`); un `role="status"` con testo
 * `sr-only` annuncia lo stato di caricamento agli screen reader.
 */
function TeamMemberDashboardSkeleton({
  onBack,
  backLabel,
}: {
  onBack: () => void;
  backLabel: string;
}) {
  return (
    <section aria-labelledby="team-member-title">
      <PageHeader
        title="Dashboard persona"
        titleId="team-member-title"
        subtitle="Dashboard del membro del team."
        actions={
          <Button type="button" variant="ghost" onClick={onBack}>
            {backLabel}
          </Button>
        }
      />

      {/* Live region per l'annuncio dello stato; i placeholder sono decorativi. */}
      <p role="status" className="sr-only">
        Caricamento della dashboard…
      </p>

      {/* Riga superiore: hero a sinistra, tre KPI impilati a destra. */}
      <div className="dashboard__top">
        <div className="dashboard-skeleton__hero">
          <Skeleton width="40%" height="0.75rem" />
          <Skeleton width="70%" height="2rem" />
          <Skeleton height="0.9rem" />
          <Skeleton width="85%" height="0.9rem" />
          <Skeleton width="55%" height="2.5rem" radius="var(--radius-pill)" />
        </div>

        <div className="dashboard__kpis">
          {[0, 1, 2].map((i) => (
            <div key={i} className="dashboard-skeleton__kpi">
              <Skeleton width="60%" height="0.7rem" />
              <Skeleton width="45%" height="1.6rem" />
            </div>
          ))}
        </div>
      </div>

      {/* Sezione "Skill valutate": titolo + righe tabella. */}
      <div className="dashboard-skeleton__section-title">
        <Skeleton width="200px" height="1.4rem" />
      </div>
      <div className="dashboard-skeleton__hero" style={{ gap: 'var(--space-3)' }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height="1.4rem" />
        ))}
      </div>
    </section>
  );
}

export default function TeamMemberDashboard() {
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const { userId } = useParams<{ userId: string }>();

  // Provenienza opzionale propagata dal chiamante come `state.from`: quando si
  // arriva da un'esplorazione (`/explore/:id`), il tasto "indietro" torna lì
  // invece che alla sezione Insights/Team (default). Si accettano solo i path
  // `/explore/` per non interferire con l'ingresso normale da `/insights`.
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const backFrom =
    typeof fromState === 'string' && fromState.startsWith('/explore/')
      ? fromState
      : null;
  const backLabel = backFrom !== null ? '← Torna all\'esplorazione' : '← Torna al Team';

  // L'import Credly per un altro utente è riservato all'admin lato API
  // (`POST /api/users/:id/certifications/import-credly` è `requireAdmin`).
  const isAdmin = role === 'admin';

  // Le competenze oltre le prime 4 (hero + 3 in griglia) sono nascoste finché
  // non si espande.
  const [showAll, setShowAll] = useState(false);
  // Le professioni oltre le prime 3 (con match > 0%) sono dietro un accordion.
  const [showAllJobs, setShowAllJobs] = useState(false);
  // Le skill oltre le prime 10 sono nascoste finché non si espande.
  const [showAllSkills, setShowAllSkills] = useState(false);
  // Le certificazioni oltre le prime 3 sono nascoste finché non si espande.
  const [showAllCerts, setShowAllCerts] = useState(false);
  // Riga di cui è aperto il dettaglio di calcolo dello Score (o `null`). La
  // vista Team resta read-only: il modale è solo informativo.
  const [scoreDetailRow, setScoreDetailRow] = useState<AssessmentView | null>(null);

  const { data, isLoading, isError, error, refetch } =
    useQuery<TeamMemberDashboardDto>({
      queryKey: ['dashboard', 'users', userId],
      queryFn: () =>
        api.get<TeamMemberDashboardDto>(
          `/api/dashboard/users/${encodeURIComponent(userId ?? '')}`,
        ),
      enabled: userId !== undefined,
    });

  // Elenco di TUTTE le skill valutate della persona (Self/AI/Reviewer/Manager +
  // livello), in sola lettura. Stessa query key usata dal dettaglio competenza
  // team, così la cache è condivisa.
  const assessmentsQuery = useQuery<AssessmentView[]>({
    queryKey: ['dashboard', 'users', userId, 'assessments'],
    queryFn: () =>
      api.get<AssessmentView[]>(
        `/api/dashboard/users/${encodeURIComponent(userId ?? '')}/assessments`,
      ),
    enabled: userId !== undefined,
  });

  // Certificazioni della persona (sola lettura), per il blocco "Certificazioni".
  const certificationsQuery = useQuery<UserCertificationDto[]>({
    queryKey: ['dashboard', 'users', userId, 'certifications'],
    queryFn: () =>
      api.get<UserCertificationDto[]>(
        `/api/dashboard/users/${encodeURIComponent(userId ?? '')}/certifications`,
      ),
    enabled: userId !== undefined,
  });

  // Posizionamento del membro sulle professioni (per la sezione "Professioni").
  // Non blocca il render: finché non è disponibile l'elenco è vuoto.
  const jobPositioningQuery = useQuery<JobPositioningDto>({
    queryKey: ['dashboard', 'users', userId, 'job-positioning'],
    queryFn: () =>
      api.get<JobPositioningDto>(
        `/api/dashboard/users/${encodeURIComponent(userId ?? '')}/job-positioning`,
      ),
    enabled: userId !== undefined,
  });

  // Import delle certificazioni del membro dal suo profilo Credly (solo admin).
  // Al termine si invalida la sua lista così le nuove certificazioni compaiono.
  const importCredly = useMutation({
    mutationFn: () =>
      api.post<ImportCredlyResponse>(
        `/api/users/${encodeURIComponent(userId ?? '')}/certifications/import-credly`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['dashboard', 'users', userId, 'certifications'],
      });
    },
  });

  // Import delle certificazioni del membro da LinkedIn (solo admin, e solo se il
  // membro ha un collegamento LinkedIn attivo: l'import richiede un token di
  // consenso già presente, senza fallback nome-cognome come Credly).
  const importLinkedin = useMutation({
    mutationFn: () =>
      api.post<ImportLinkedinResponse>(
        `/api/users/${encodeURIComponent(userId ?? '')}/certifications/import-linkedin`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['dashboard', 'users', userId, 'certifications'],
      });
    },
  });

  // Catalogo skill: serve solo a recuperare la descrizione (assente in
  // `AssessmentView`) da mostrare nell'hint accanto al nome skill.
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });
  const descriptionBySkillId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const s of skillsQuery.data ?? []) map.set(s.id, s.description);
    return map;
  }, [skillsQuery.data]);

  // Configurazione di scoring (pesi fonti/assi, `minScore`): serve al modale di
  // dettaglio per ricostruire i passaggi del calcolo. Leggibile da qualsiasi
  // utente autenticato; in attesa/errore ripiega sui default applicativi.
  const scoringConfigQuery = useQuery({
    queryKey: ['settings', 'scoring'],
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
    staleTime: 5 * 60 * 1000,
  });
  const scoringConfig = scoringConfigQuery.data ?? DEFAULT_SCORING_CONFIG;

  // Risolutore id skill → nome, per i chip nelle card certificazione.
  const skillNameById = useMemo(() => {
    const byId = new Map((skillsQuery.data ?? []).map((s) => [s.id, s.name]));
    return (id: string) => byId.get(id);
  }, [skillsQuery.data]);

  // Certificazioni: prime TOP_CERTIFICATIONS_COUNT visibili, le altre in accordion.
  // Certificazioni ordinate per ultima conseguita (desc); le senza data in coda.
  const certifications = sortCertificationsByIssuedDesc(
    certificationsQuery.data ?? [],
    (c) => c.issuedAt,
    (c) => c.name,
  );
  const topCertifications = certifications.slice(0, TOP_CERTIFICATIONS_COUNT);
  const otherCertifications = certifications.slice(TOP_CERTIFICATIONS_COUNT);
  const hasOtherCertifications = otherCertifications.length > 0;

  const skillRows = assessmentsQuery.data ?? [];
  // Ordinamento di default: Score (media pesata) decrescente, con le skill senza
  // voto in coda e il nome come tie-break deterministico.
  const orderedSkillRows = useMemo(
    () =>
      [...skillRows].sort((a, b) => {
        const byScore = compareScore(a, b, 'desc');
        if (byScore !== 0) return byScore;
        return a.skillName.localeCompare(b.skillName, 'it', { sensitivity: 'base' });
      }),
    [skillRows],
  );
  // Le prime TOP_SKILLS_COUNT sono sempre visibili; le altre stanno dietro
  // l'accordion "Altre skill".
  const topSkillRows = orderedSkillRows.slice(0, TOP_SKILLS_COUNT);
  const otherSkillRows = orderedSkillRows.slice(TOP_SKILLS_COUNT);
  const hasOtherSkills = otherSkillRows.length > 0;

  // Mostra la colonna AI solo se la persona ha almeno un Livello AI registrato.
  const showAiColumn = skillRows.some((row) => row.aiLevel !== null);
  const skillColumns = useMemo(
    () =>
      buildSkillColumns(showAiColumn, descriptionBySkillId, (row) =>
        setScoreDetailRow(row),
      ),
    [showAiColumn, descriptionBySkillId],
  );

  const backToTeam = () => {
    void navigate(backFrom ?? '/insights');
  };

  if (isLoading) {
    return <TeamMemberDashboardSkeleton onBack={backToTeam} backLabel={backLabel} />;
  }

  if (isError || !data) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare la dashboard.';
    return (
      <section aria-labelledby="team-member-title">
        <PageHeader
          title="Dashboard persona"
          titleId="team-member-title"
          subtitle="Dashboard del membro del team."
          actions={
            <Button type="button" variant="ghost" onClick={backToTeam}>
              {backLabel}
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={() => void refetch()}>
            Riprova
          </Button>
        </div>
      </section>
    );
  }

  const { displayName, departmentName, dashboard } = data;

  // Il pulsante d'import si mostra a tutti gli admin, anche se il membro non ha
  // un profilo Credly collegato: in quel caso il backend prova comunque a
  // individuarlo dallo slug `nome-cognome` (e, se lo trova, lo collega). Serve
  // solo il permesso admin (l'endpoint per-utente è `requireAdmin`).
  const canImportCredly = isAdmin;
  // L'import LinkedIn per conto del membro richiede, oltre al permesso admin,
  // che il membro abbia un collegamento LinkedIn attivo (token di consenso).
  const canImportLinkedin = isAdmin && data.linkedinLinked === true;
  const { evaluatedSkillCount, reviewsReceived, competencyMatches } = dashboard;
  const orderedMatches = sortByMatchDesc(competencyMatches);
  // La competenza con match più elevato è la "hero" (in grande, in cima). Le 3
  // successive vanno nella griglia sotto la hero; le restanti dietro l'accordion.
  const [heroMatch, ...restMatches] = orderedMatches;
  const gridMatches = restMatches.slice(0, GRID_MATCHES_COUNT);
  const otherMatches = restMatches.slice(GRID_MATCHES_COUNT);
  const hasOtherMatches = otherMatches.length > 0;

  // Professioni: solo quelle con almeno un match > 0%, ordinate per rankScore.
  // Le prime 3 vanno in griglia; le restanti dietro l'accordion "Altre professioni".
  const rankedJobs = sortJobsByMatchDesc(
    (jobPositioningQuery.data?.jobMatches ?? []).filter((j) => j.matchPercent > 0),
  );
  const topJobs = rankedJobs.slice(0, JOB_GRID_COUNT);
  const otherJobs = rankedJobs.slice(JOB_GRID_COUNT);
  const hasOtherJobs = otherJobs.length > 0;

  // Il dettaglio competenza si apre nel contesto Team (sola lettura).
  const detailPath = (competenceId: string) =>
    `/insights/team/${encodeURIComponent(data.userId)}/competences/${encodeURIComponent(competenceId)}`;

  // Il dettaglio professione si apre nel contesto Team (sola lettura): mostra il
  // posizionamento del membro, non dell'utente loggato.
  const jobDetailPath = (jobId: string) =>
    `/insights/team/${encodeURIComponent(data.userId)}/jobs/${encodeURIComponent(jobId)}`;

  return (
    <section aria-labelledby="team-member-title">
      <PageHeader
        title={displayName}
        titleId="team-member-title"
        subtitle="Dashboard del membro del team (sola lettura)."
        titleAdornment={
          departmentName !== null ? (
            <Badge tone="neutral" className="team-member-dashboard__department-badge">
              {departmentName}
            </Badge>
          ) : undefined
        }
        actions={
          <div className="team-member-dashboard__header-actions">
            <Button type="button" variant="ghost" onClick={backToTeam}>
              {backLabel}
            </Button>
            {canImportCredly && (
              <Button
                type="button"
                variant="primary"
                loading={importCredly.isPending}
                disabled={importCredly.isPending}
                onClick={() => importCredly.mutate()}
              >
                Importa da Credly
              </Button>
            )}
            {canImportLinkedin && (
              <Button
                type="button"
                variant="primary"
                loading={importLinkedin.isPending}
                disabled={importLinkedin.isPending}
                onClick={() => importLinkedin.mutate()}
              >
                Importa da LinkedIn
              </Button>
            )}
          </div>
        }
      />

      {canImportCredly && importCredly.isError && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Alert tone="error" onDismiss={() => importCredly.reset()}>
            {importCredly.error instanceof Error
              ? importCredly.error.message
              : 'Import non riuscito. Se il membro non ha un profilo Credly collegato, ' +
                'collegalo manualmente e riprova.'}
          </Alert>
        </div>
      )}
      {canImportLinkedin && importLinkedin.isError && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Alert tone="error" onDismiss={() => importLinkedin.reset()}>
            {importLinkedin.error instanceof Error
              ? importLinkedin.error.message
              : 'Import da LinkedIn non riuscito. Se la sessione del membro è scaduta, ' +
                'deve ricollegare LinkedIn dal proprio profilo.'}
          </Alert>
        </div>
      )}

      {/* Riga superiore: hero (competenza principale) a sinistra, KPI compatti a
          destra (stessi KPI della dashboard personale, che condivide il
          payload). Senza competenze la hero è sostituita dall'empty state. */}
      <div className="dashboard__top">
        <div className="dashboard__hero">
          {heroMatch ? (
            <CompetencyMatchCard match={heroMatch} detailPath={detailPath} hero />
          ) : (
            <EmptyState role="status">
              Nessun match disponibile per questa persona.
            </EmptyState>
          )}
        </div>
        <div className="dashboard__kpis">
          <article className="kpi-card" aria-label="Skill valutate">
            <span className="kpi-card__label">Skill valutate</span>
            <span className="kpi-card__value">{evaluatedSkillCount}</span>
          </article>

          <article className="kpi-card" aria-label="Certificazioni ottenute">
            <span className="kpi-card__label">Certificazioni</span>
            <span className="kpi-card__value">{certifications.length}</span>
          </article>

          <article className="kpi-card" aria-label="Recensioni ricevute">
            <span className="kpi-card__label">Recensioni ricevute</span>
            {/* Totale valutatori distinti; in empty state mostra `0`. */}
            <span className="kpi-card__value">{reviewsReceived.total}</span>
            {/* Ripartizione per ruolo, mostrata solo se c'è almeno una recensione. */}
            {reviewsReceived.total > 0 && (
              <span className="kpi-card__hint">
                {reviewsReceived.reviewerCount} reviewer · {reviewsReceived.managerCount}{' '}
                manager
              </span>
            )}
          </article>
        </div>
      </div>

      {/* Griglia delle competenze successive alla hero, ordinata per rankScore
          desc. Le competenze oltre le prime 4 sono in un accordion "Altre
          competenze" apri/chiudi. */}
      {orderedMatches.length > 0 && (
        <section aria-labelledby="team-member-matches-title">
          <div className="dashboard__matches-heading">
            <h2 id="team-member-matches-title">Competenze</h2>
            <ScoreGuide />
          </div>
          {gridMatches.length > 0 && (
            <ul className="dashboard__matches">
              {gridMatches.map((match) => (
                <li key={match.competenceId}>
                  <CompetencyMatchCard match={match} detailPath={detailPath} />
                </li>
              ))}
            </ul>
          )}

          {hasOtherMatches && (
            <>
              <button
                type="button"
                className="dashboard__matches-accordion"
                aria-expanded={showAll}
                aria-controls="team-member-other-matches"
                onClick={() => setShowAll((prev) => !prev)}
              >
                <span className="dashboard__matches-accordion-label">
                  Altre competenze ({otherMatches.length})
                </span>
                <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
              </button>

              {showAll && (
                <ul
                  id="team-member-other-matches"
                  className="dashboard__matches-list"
                  aria-label="Altre competenze"
                >
                  {otherMatches.map((match) => (
                    <li key={match.competenceId}>
                      <CompetencyMatchCard match={match} detailPath={detailPath} compact />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {/* Elenco di tutte le singole skill valutate della persona, in sola
          lettura: Self/AI/Reviewer/Manager + livello e media pesata. */}
      <section
        aria-labelledby="team-member-skills-title"
        className="team-member-dashboard__skills"
      >
        <h2 id="team-member-skills-title">Skill valutate</h2>
        {/* Prime TOP_SKILLS_COUNT skill (Score più alto) sempre visibili. */}
        <DataTable
          rows={topSkillRows}
          columns={skillColumns}
          rowKey={(row) => row.skillId}
          loading={assessmentsQuery.isLoading}
          error={
            assessmentsQuery.isError
              ? 'Impossibile caricare le skill di questa persona.'
              : null
          }
          emptyMessage="Questa persona non ha ancora skill valutate."
          defaultSort={{ key: 'score', direction: 'desc' }}
          paginated={false}
        />

        {/* Le skill oltre le prime 10 stanno dietro un accordion "Altre skill". */}
        {hasOtherSkills && (
          <>
            <button
              type="button"
              className="dashboard__matches-accordion"
              aria-expanded={showAllSkills}
              aria-controls="team-member-other-skills"
              onClick={() => setShowAllSkills((prev) => !prev)}
            >
              <span className="dashboard__matches-accordion-label">
                Altre skill ({otherSkillRows.length})
              </span>
              <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
            </button>

            {showAllSkills && (
              <div
                id="team-member-other-skills"
                className="team-member-dashboard__other-skills"
              >
                <DataTable
                  rows={otherSkillRows}
                  columns={skillColumns}
                  rowKey={(row) => row.skillId}
                  emptyMessage="Nessuna altra skill."
                  defaultSort={{ key: 'score', direction: 'desc' }}
                  paginated={false}
                />
              </div>
            )}
          </>
        )}
      </section>

      {/* Professioni: le 3 principali (per rankScore) in griglia e le altre con
          match > 0% dietro l'accordion "Altre professioni". La sezione compare
          solo se c'è almeno una professione con match. Le card aprono il
          dettaglio professione nel contesto Team (`/team/:userId/jobs/:jobId`),
          che mostra il posizionamento del membro. */}
      {topJobs.length > 0 && (
        <section aria-labelledby="team-member-jobs-title">
          <div className="dashboard__matches-heading">
            <h2 id="team-member-jobs-title">Professioni</h2>
            <JobScoreGuide />
          </div>

          <ul className="dashboard__matches">
            {topJobs.map((job) => (
              <li key={job.jobId}>
                <JobMatchCard job={job} detailPath={jobDetailPath} />
              </li>
            ))}
          </ul>

          {hasOtherJobs && (
            <>
              <button
                type="button"
                className="dashboard__matches-accordion"
                aria-expanded={showAllJobs}
                aria-controls="team-member-other-jobs"
                onClick={() => setShowAllJobs((prev) => !prev)}
              >
                <span className="dashboard__matches-accordion-label">
                  Altre professioni ({otherJobs.length})
                </span>
                <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
              </button>

              {showAllJobs && (
                <ul
                  id="team-member-other-jobs"
                  className="dashboard__matches-list"
                  aria-label="Altre professioni"
                >
                  {otherJobs.map((job) => (
                    <li key={job.jobId}>
                      <JobMatchCard job={job} compact detailPath={jobDetailPath} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {/* Certificazioni della persona: le prime TOP_CERTIFICATIONS_COUNT sempre
          visibili, le altre dietro un accordion "Altre certificazioni". */}
      <section
        aria-labelledby="team-member-certifications-title"
        className="team-member-dashboard__certifications"
      >
        <h2 id="team-member-certifications-title">Certificazioni</h2>

        {certificationsQuery.isLoading ? (
          <Loading>Caricamento delle certificazioni…</Loading>
        ) : certificationsQuery.isError ? (
          <Alert tone="error">
            Impossibile caricare le certificazioni di questa persona.
          </Alert>
        ) : certifications.length === 0 ? (
          <EmptyState role="status">
            Questa persona non ha ancora certificazioni.
          </EmptyState>
        ) : (
          <>
            <ul className="certification-list">
              {topCertifications.map((c) => (
                <li
                  key={c.certificationId}
                  className="certification-card certification-card--linked"
                >
                  {/* "Stretched link": copre l'intera card e apre il dettaglio
                      dell'istanza di certificazione del membro (contesto Team).
                      Gli anchor interni restano cliccabili (z-index superiore). */}
                  <Link
                    to={`/insights/team/${encodeURIComponent(data.userId)}/certifications/${encodeURIComponent(c.certificationId)}`}
                    className="certification-card__stretched-link"
                    aria-label={`Apri il dettaglio di ${c.name}`}
                  />
                  <CertificationCard certification={c} skillNameById={skillNameById} />
                </li>
              ))}
            </ul>

            {hasOtherCertifications && (
              <>
                <button
                  type="button"
                  className="dashboard__matches-accordion"
                  aria-expanded={showAllCerts}
                  aria-controls="team-member-other-certifications"
                  onClick={() => setShowAllCerts((prev) => !prev)}
                >
                  <span className="dashboard__matches-accordion-label">
                    Altre certificazioni ({otherCertifications.length})
                  </span>
                  <span
                    className="dashboard__matches-accordion-icon"
                    aria-hidden="true"
                  />
                </button>

                {showAllCerts && (
                  <ul
                    id="team-member-other-certifications"
                    className="certification-list team-member-dashboard__other-certifications"
                    aria-label="Altre certificazioni"
                  >
                    {otherCertifications.map((c) => (
                      <li
                        key={c.certificationId}
                        className="certification-card certification-card--linked"
                      >
                        <Link
                          to={`/insights/team/${encodeURIComponent(data.userId)}/certifications/${encodeURIComponent(c.certificationId)}`}
                          className="certification-card__stretched-link"
                          aria-label={`Apri il dettaglio di ${c.name}`}
                        />
                        <CertificationCard
                          certification={c}
                          skillNameById={skillNameById}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </section>

      {/* Dettaglio del calcolo dello Score della skill selezionata, aperto dal
          badge Score cliccabile nella sezione "Skill valutate". Informativo: la
          vista Team resta in sola lettura. Il modale usa `createPortal`, quindi
          la posizione nel JSX non influisce sul layout. */}
      {scoreDetailRow !== null && (
        <ScoreDetailModal
          row={scoreDetailRow}
          config={scoringConfig}
          onClose={() => setScoreDetailRow(null)}
        />
      )}
    </section>
  );
}
